import React, { useRef ,useState } from 'react';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import {
    useGetExercisesQuery,
    useAddExerciseMutation,
    useUpdateExerciseMutation,
    useDeleteExerciseMutation,
    useGetTechnologiesQuery, // Assuming exercises need technologies for dropdown
    useGetNiveauxQuery // Assuming exercises need niveaux for dropdown
} from '../../redux/api/coachApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ExercisesView = () => {
    const [newExercise, setNewExercise] = useState({
        intitule: '',
        maximum_marks: 4,
        technology: '',
        niveau: '',
        resolution_time: 30,
        reponses: [
            {
                test_unitaire: ''
            }
        ]
    });
    const [editingExercise, setEditingExercise] = useState(null);
    const [error, setError] = useState('');
    const formRef = useRef(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [exerciseToDelete, setExerciseToDelete] = useState(null);

    
    // RTK Query hooks
    const { data: exercises, isLoading, error: fetchError } = useGetExercisesQuery();
    const [addExercise, { isLoading: isAdding }] = useAddExerciseMutation();
    const [updateExercise, { isLoading: isUpdating }] = useUpdateExerciseMutation();
    const [deleteExercise, { isLoading: isDeleting }] = useDeleteExerciseMutation();

    // Fetch Technologies and Niveaux for dropdowns
    const { data: technologies, isLoading: isLoadingTechnologies } = useGetTechnologiesQuery();
    const { data: niveaux, isLoading: isLoadingNiveaux } = useGetNiveauxQuery();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newExercise.intitule.trim() || !newExercise.technology || !newExercise.niveau) {
            setError('Intitulé, Technologie et Niveau sont requis.');
            return;
        }
        setError('');

        // Inject niveau & technology dans chaque réponse
        const enrichedExercise = {
            ...newExercise,
            reponses: newExercise.reponses.map((rep) => {
                const testUnitArray = Array.isArray(rep.test_unitaire)
                    ? rep.test_unitaire.filter(line => line.trim().length > 0)
                    : [];


                return {
                    ...rep,
                    test_unitaire: JSON.stringify(testUnitArray),
                    niveau: newExercise.niveau,
                    technology: newExercise.technology,
                };
            })
        };


        try {
            if (editingExercise) {
                await updateExercise({ id: editingExercise.id, ...enrichedExercise }).unwrap();
                toast.success("Exercice mis à jour !");
            } else {
                await addExercise(enrichedExercise).unwrap();
                toast.success("Exercice ajouté avec succès !");
            }

            // Reset
            setNewExercise({
                intitule: '',
                maximum_marks: 4,
                technology: '',
                niveau: '',
                resolution_time: 30,
                reponses: [
                    {
                        test_unitaire: ''
                    }
                ]
            });
            setEditingExercise(null);
        } catch (err) {
            const backendError = err?.data;
            setError(backendError ? JSON.stringify(backendError) : "Erreur inconnue.");
            console.error("Erreur envoyée par le backend :", backendError);
        }
    };

    const handleEdit = (exercise) => {
        formRef.current?.scrollIntoView({ behavior: 'smooth' });
        setEditingExercise(exercise);
        // Populate form with existing exercise data, handle potential missing reponses
        setNewExercise({
            intitule: exercise.intitule || '',
            maximum_marks: exercise.maximum_marks || 4,
            technology: exercise.technology || '',
            niveau: exercise.niveau || '',
            resolution_time: exercise.resolution_time || 30,
            reponses: exercise.reponses && exercise.reponses.length > 0
            ? exercise.reponses.map(rep => {
                let parsedTest = '';
                try {
                const parsed = JSON.parse(rep.test_unitaire);
                parsedTest = Array.isArray(parsed) ? parsed : [parsed];
                } catch (e) {
                parsedTest = [rep.test_unitaire];
                }

                return {
                    ...rep,
                    test_unitaire: parsedTest
                };
            })
            : [{ 
                test_unitaire: '' }
            ]

        });
    };

    const confirmDelete = async () => {
        try {
            await deleteExercise(exerciseToDelete).unwrap();
            toast.error("Exercice supprimé.");
        } catch (err) {
            toast.error("Erreur lors de la suppression.");
            console.error("Erreur lors de la suppression :", err);
        } finally {
            setShowDeleteModal(false);
            setExerciseToDelete(null);
        }
    };



    const handleDelete = (id) => {
        setExerciseToDelete(id);
        setShowDeleteModal(true);
    };

    const handleUpdateResponse = (index, field, value) => {
        const updatedResponses = [...newExercise.reponses];
        updatedResponses[index] = {
            ...updatedResponses[index],
            [field]: value
        };
        setNewExercise(prev => ({
            ...prev,
            reponses: updatedResponses
        }));
    };

    // const handleDeleteResponse = (index) => {
    //     setNewExercise(prev => ({
    //         ...prev,
    //         reponses: prev.reponses.filter((_, i) => i !== index)
    //     }));
    // };

    if (isLoading || isLoadingTechnologies || isLoadingNiveaux) {
        return <div className="loading">Chargement des exercices...</div>;
    }

    if (fetchError) {
        return <div className="error">Erreur lors du chargement des exercices: {fetchError.message}</div>;
    }

    return (
        <div className="exercises-view-container">
            {/* Form Card */}
            <div className="form-card" ref={formRef}>
                <h2>{editingExercise ? "Modifier l'Exercice" : 'Ajouter un Exercice'}</h2>
                {error && <div className="error-message">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="intitule">Intitulé</label>
                        <textarea
                            id="intitule"
                            className="form-control"
                            rows={4}
                            value={newExercise.intitule}
                            onChange={(e) => setNewExercise({ ...newExercise, intitule: e.target.value })}
                            placeholder={`Entrez l'intitulé de l'exercice`}
                        />

                    </div>
                    <div className="form-group">
                        <label htmlFor="maximum_marks">Notes Maximales</label>
                        <input
                            type="number"
                            id="maximum_marks"
                            className="form-control"
                            value={newExercise.maximum_marks}
                            onChange={(e) => setNewExercise({ ...newExercise, maximum_marks: parseInt(e.target.value) })}
                            min="1"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="technology">Technologie</label>
                        <select
                            id="technology"
                            className="form-control"
                            value={newExercise.technology}
                            onChange={(e) => setNewExercise({ ...newExercise, technology: e.target.value })}
                        >
                            <option value="">Sélectionner une technologie</option>
                            {technologies?.map(tech => (
                                <option key={tech.id} value={tech.id}>{tech.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="niveau">Niveau</label>
                        <select
                            id="niveau"
                            className="form-control"
                            value={newExercise.niveau}
                            onChange={(e) => setNewExercise({ ...newExercise, niveau: e.target.value })}
                        >
                            <option value="">Sélectionner un niveau</option>
                            {niveaux?.map(niv => (
                                <option key={niv.id} value={niv.id}>{niv.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="resolution_time">Temps de Résolution (minutes)</label>
                        <input
                            type="number"
                            id="resolution_time"
                            className="form-control"
                            value={newExercise.resolution_time}
                            onChange={(e) => setNewExercise({ ...newExercise, resolution_time: parseInt(e.target.value) })}
                            min="1"
                        />
                    </div>

                    {/* Réponses Section */}
                    <div className="responses-section">
                        <h3>Réponses</h3>
                        {newExercise.reponses.map((reponse, index) => (
                            <div key={index} className="response-item">
                                <div className="form-group">
                                <label>Tests Unitaires</label>
                                {(Array.isArray(reponse.test_unitaire) ? reponse.test_unitaire : ['']).map((test, testIndex) => (
                                    <div key={testIndex} className="flex items-center gap-2 mb-2">
                                    <input
                                        type="text"
                                        className="form-control flex-1"
                                        placeholder={`Test unitaire ${testIndex + 1}`}
                                        value={test}
                                        onChange={(e) => {
                                        const updatedTests = [...(reponse.test_unitaire || [])];
                                        updatedTests[testIndex] = e.target.value;

                                        handleUpdateResponse(index, 'test_unitaire', updatedTests);
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="btn-icon"
                                        onClick={() => {
                                        const updatedTests = reponse.test_unitaire.filter((_, i) => i !== testIndex);
                                        handleUpdateResponse(index, 'test_unitaire', updatedTests);
                                        }}
                                        disabled={(reponse.test_unitaire?.length || 1) <= 1}
                                        title="Supprimer ce test"
                                    >
                                        <FiTrash2 />
                                    </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="btn-add-response mt-1"
                                    onClick={() => {
                                    const updatedTests = [...(reponse.test_unitaire || [])];
                                    updatedTests.push('');
                                    handleUpdateResponse(index, 'test_unitaire', updatedTests);
                                    }}
                                >
                                    <FiPlus /> Ajouter un test
                                </button>


                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        type="submit"
                        className="btn-add-middle"
                        disabled={isAdding || isUpdating}
                    >
                        {editingExercise ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                </form>
            </div>

            {/* List Card */}
            <div className="list-card">
                <h2>Liste des Exercices</h2>
                {exercises && exercises.length > 0 ? (
                    <div className="list-items-container">
                        {exercises.map(exercise => (
                            <div key={exercise.id} className="list-item">
                                <div className="content">
                                    {/* <h3>{exercise.intitule}</h3> */}
                                    <h3 style={{ whiteSpace: 'pre-wrap' }}>{exercise.intitule}</h3>
                                    <p>Technologie: {technologies?.find(tech => tech.id === exercise.technology)?.name || 'N/A'}</p>
                                    <p>Niveau: {niveaux?.find(niv => niv.id === exercise.niveau)?.name || 'N/A'}</p>
                                </div>
                                <div className="actions">
                                    <button
                                        className="btn-icon"
                                        onClick={() => handleEdit(exercise)}
                                        disabled={isUpdating}
                                    >
                                        <FiEdit2 />
                                    </button>
                                    <button
                                        className="btn-icon"
                                        onClick={() => handleDelete(exercise.id)}
                                        disabled={isDeleting}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="no-data">Aucun exercice trouvé.</p>
                )}
            </div>
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
                        <h2 className="text-xl font-semibold mb-4">Confirmer la suppression</h2>
                        <p className="mb-6">Voulez-vous vraiment supprimer cet exercice ? Cette action est irréversible.</p>
                        <div className="flex justify-end gap-4">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                            >
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
};

export default ExercisesView; 